// Inline meter-capture field inside a work-order task (LOCAL demo). Renders
// like any other numeric task field — just a label, input, and subtitle.
// There is no "Record" button: the value is staged in sessionStorage and
// written to the asset meter (source WORK_ORDER) when the work order is
// marked Complete. Any crossed PM threshold is evaluated at that same moment
// by the workOrderUpdate GraphQL handler.

import { useContext, useEffect, useState } from 'react';

import WorkOrderContext from '@atom/components/workOrderDetail/WorkOrderContext';
import { TextField } from '@atom/mui';

import classes from './taskMeterCapture.module.css';

const RUNTIME_ATTR = 'attr-pump-runtime-hours';

// sessionStorage key where the pending reading is staged until WO completes.
const pendingKey = (workOrderId: string): string =>
  `pendingCapture_${workOrderId}`;

const isRuntimeCaptureTask = (name?: string): boolean =>
  !!name && /runtime reading/i.test(name);

const TaskMeterCapture = () => {
  const { workOrderDetail, task } = useContext(WorkOrderContext);
  const assetId = (workOrderDetail as any)?.inventoryAssetId as
    | string
    | undefined;
  const workOrderId = (workOrderDetail as any)?.id as string | undefined;

  const [value, setValue] = useState('');

  const eligible = isRuntimeCaptureTask((task as any)?.name) && !!assetId;

  // Pre-fill with the current computed meter value on mount and prime the
  // sessionStorage entry so a completion without any manual edit still records
  // the live value.
  useEffect(() => {
    if (!eligible || !assetId || !workOrderId) return;
    fetch(`/api/v1/assets/${assetId}/meters/${RUNTIME_ATTR}/readings`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        const cv = d?.currentValue;
        if (cv == null) return;
        const rounded = Math.round(cv * 10) / 10;
        const str = String(rounded);
        setValue(str);
        sessionStorage.setItem(
          pendingKey(workOrderId),
          JSON.stringify({ assetId, attributeId: RUNTIME_ATTR, value: rounded }),
        );
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId, eligible]);

  if (!eligible) return null;

  const handleChange = (e: any) => {
    const str = e.target.value;
    setValue(str);
    const num = parseFloat(str);
    if (Number.isFinite(num) && workOrderId && assetId) {
      sessionStorage.setItem(
        pendingKey(workOrderId),
        JSON.stringify({ assetId, attributeId: RUNTIME_ATTR, value: num }),
      );
    }
  };

  return (
    <div className={classes.capture}>
      <div className={classes.label}>Runtime Hours (record at close)</div>
      <TextField
        type="number"
        value={value}
        onChange={handleChange}
        style={{ maxWidth: '18rem', display: 'block' }}
      />
      <div className={classes.sub}>
        PM schedule field — records to the asset and triggers when this work
        order is marked complete.
      </div>
    </div>
  );
};

export default TaskMeterCapture;
